import { Item } from "../redux/interfaces/reducersInterfaces";

const ATTIO_PARAM_KEYS = ["attio", "attio?"] as const;
const ATTIO_SESSION_KEY = "attio_phone_query";
const ATTIO_SEARCH_IN_PROGRESS_KEY = "attio_search_in_progress";
const ATTIO_SEARCH_DONE_KEY = "attio_search_done";

function readAttioPhoneFromSearch(search: string): string | null {
  if (!search) return null;

  const params = new URLSearchParams(search);
  for (const key of ATTIO_PARAM_KEYS) {
    const value = params.get(key);
    if (value?.trim()) {
      return value.trim();
    }
  }

  return null;
}

/** Capture Attio phone from URL immediately (call before React renders). */
export function captureAttioPhoneFromUrl(): void {
  const fromUrl = readAttioPhoneFromSearch(window.location.search);
  if (fromUrl) {
    sessionStorage.setItem(ATTIO_SESSION_KEY, fromUrl);
    sessionStorage.removeItem(ATTIO_SEARCH_DONE_KEY);
  }
}

/** Read phone number from Attio deep-link query params. */
export function getAttioPhoneFromUrl(
  search: string = window.location.search
): string | null {
  return readAttioPhoneFromSearch(search);
}

/** Read persisted Attio phone (URL first, then pending sessionStorage). */
export function getAttioPhoneForSearch(): string | null {
  const fromUrl = readAttioPhoneFromSearch(window.location.search);
  if (fromUrl) {
    sessionStorage.setItem(ATTIO_SESSION_KEY, fromUrl);
    sessionStorage.removeItem(ATTIO_SEARCH_DONE_KEY);
    return fromUrl;
  }

  if (isAttioSearchDone()) return null;

  return sessionStorage.getItem(ATTIO_SESSION_KEY);
}

/** Clear Attio session flags so normal root visits load all chats. */
export function resetAttioSession(): void {
  sessionStorage.removeItem(ATTIO_SESSION_KEY);
  sessionStorage.removeItem(ATTIO_SEARCH_IN_PROGRESS_KEY);
  sessionStorage.removeItem(ATTIO_SEARCH_DONE_KEY);
}

export function isAttioSearchDone(): boolean {
  return sessionStorage.getItem(ATTIO_SEARCH_DONE_KEY) === "1";
}

export function isAttioSearchInProgress(): boolean {
  return sessionStorage.getItem(ATTIO_SEARCH_IN_PROGRESS_KEY) === "1";
}

export function markAttioSearchInProgress(): void {
  sessionStorage.setItem(ATTIO_SEARCH_IN_PROGRESS_KEY, "1");
}

export function markAttioSearchComplete(): void {
  sessionStorage.setItem(ATTIO_SEARCH_DONE_KEY, "1");
  sessionStorage.removeItem(ATTIO_SEARCH_IN_PROGRESS_KEY);
  sessionStorage.removeItem(ATTIO_SESSION_KEY);
}

/** Remove Attio params from the URL without reloading the page. */
export function clearAttioParamsFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;

  for (const key of ATTIO_PARAM_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  if (changed) {
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  }
}

function normalizePhoneForMatch(phone: string | number): string {
  return String(phone).replace(/\D/g, "");
}

/** Find the best recipient match for an Attio phone query. */
export function findRecipientByAttioPhone(
  items: Item[],
  phoneQuery: string
): Item | undefined {
  const normalizedQuery = normalizePhoneForMatch(phoneQuery);
  if (!normalizedQuery) return undefined;

  const exactMatch = items.find(
    (item) =>
      normalizePhoneForMatch(item.recipient.phone_number ?? "") ===
      normalizedQuery
  );
  if (exactMatch) return exactMatch;

  return items.find((item) => {
    const phone = normalizePhoneForMatch(item.recipient.phone_number ?? "");
    return (
      phone.endsWith(normalizedQuery) || normalizedQuery.endsWith(phone)
    );
  });
}

if (typeof window !== "undefined") {
  captureAttioPhoneFromUrl();
}
